import React, { useCallback, useId, useState } from "react"

import {
  Button,
  Input,
  Label,
  SelectionChip,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@coval/ui"
import { Braces, List, Plus } from "lucide-react"

import { OBJECT_VIEW_MODE, type ObjectViewMode } from "@/lib/viewModes"
import { JsonEditor } from "@/components/JsonEditor"

interface AttributesInputProps {
  attributes: Record<string, unknown>
  onChange: (attributes: Record<string, unknown>) => void
  className?: string
}

export function AttributesInput({
  attributes,
  onChange,
  className = "",
}: AttributesInputProps) {
  const keyInputId = useId()
  const valueInputId = useId()
  const [newKey, setNewKey] = useState("")
  const [newValue, setNewValue] = useState("")
  const [viewMode, setViewMode] = useState<ObjectViewMode>(
    OBJECT_VIEW_MODE.KEY_VALUE,
  )
  const attributeEntries = Object.entries(attributes || {})

  const handleAddAttribute = useCallback(() => {
    if (newKey.trim() && newValue.trim() && !attributes[newKey.trim()]) {
      onChange({
        ...attributes,
        [newKey.trim()]: newValue.trim(),
      })
      setNewKey("")
      setNewValue("")
    }
  }, [newKey, newValue, attributes, onChange])

  const handleRemoveAttribute = useCallback(
    (key: string) => {
      const newAttributes = { ...attributes }
      delete newAttributes[key]
      onChange(newAttributes)
    },
    [attributes, onChange],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault()
        handleAddAttribute()
      }
    },
    [handleAddAttribute],
  )

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex">
        <Tabs
          value={viewMode}
          onValueChange={(v) => setViewMode(v as ObjectViewMode)}
        >
          <TabsList variant="pill" aria-label="Attribute view mode">
            <TabsTrigger value={OBJECT_VIEW_MODE.KEY_VALUE} variant="pill">
              <List className="w-3.5 h-3.5" />
              List
            </TabsTrigger>
            <TabsTrigger value={OBJECT_VIEW_MODE.JSON} variant="pill">
              <Braces className="w-3.5 h-3.5" />
              JSON
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {viewMode === OBJECT_VIEW_MODE.KEY_VALUE ? (
        <>
          {attributeEntries.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {attributeEntries.map(([key, value]) => {
                const displayValue =
                  typeof value === "string"
                    ? value
                    : typeof value === "object" && value !== null
                      ? JSON.stringify(value)
                      : String(value)
                return (
                  <SelectionChip
                    key={key}
                    label={`${key}: ${displayValue}`}
                    onRemove={() => handleRemoveAttribute(key)}
                  />
                )
              })}
            </div>
          )}
          <div className="flex gap-2 items-end">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor={keyInputId}>Key</Label>
              <Input
                id={keyInputId}
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                onKeyDown={handleKeyDown}
                aria-label="Attribute key"
              />
            </div>
            <div className="flex-1 space-y-1.5">
              <Label htmlFor={valueInputId}>Value</Label>
              <Input
                id={valueInputId}
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                onKeyDown={handleKeyDown}
                aria-label="Attribute value"
              />
            </div>
            <Button
              variant="ghost"
              onClick={handleAddAttribute}
              disabled={
                !newKey.trim() ||
                !newValue.trim() ||
                !!attributes[newKey.trim()]
              }
              aria-label="Add attribute"
            >
              <Plus className="w-3 h-3" />
            </Button>
          </div>
        </>
      ) : (
        <JsonEditor
          value={attributes}
          onChange={onChange}
          height="300px"
          ariaLabel="Attributes JSON"
        />
      )}
    </div>
  )
}
